import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getSpecificationDocuments,
  uploadSpecificationDocuments,
  deleteSpecificationDocument
} from '../api/specification'

interface SpecificationDocumentsSectionProps {
  specificationId: number
  canEdit: boolean
}

const getFileSizeString = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const getFileIcon = (filename: string, mimeType?: string) => {
  const ext = filename.split('.').pop()?.toLowerCase() || ''
  
  if (mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
    return '🖼️'
  }
  if (mimeType === 'application/pdf' || ext === 'pdf') {
    return '📕'
  }
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) {
    return '📘'
  }
  if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) {
    return '📗'
  }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return '📦'
  }
  if (['txt', 'md', 'json', 'xml'].includes(ext)) {
    return '📄'
  }
  return '📁'
}

export default function SpecificationDocumentsSection({ specificationId, canEdit }: SpecificationDocumentsSectionProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)

  // Получаем документы
  const { data: documents = [], isLoading, error } = useQuery({
    queryKey: ['specificationDocuments', specificationId],
    queryFn: () => getSpecificationDocuments(specificationId),
    enabled: !!specificationId
  })

  // Мутация загрузки документов
  const uploadMutation = useMutation({
    mutationFn: (files: FileList | File[]) => uploadSpecificationDocuments(specificationId, files),
    onMutate: () => {
      setUploadProgress('Загрузка файлов...')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specificationDocuments', specificationId] })
      setUploadProgress(null)
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при загрузке документов: ' + (err.message || 'неизвестная ошибка'))
      setUploadProgress(null)
    }
  })

  // Мутация удаления документа
  const deleteMutation = useMutation({
    mutationFn: deleteSpecificationDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specificationDocuments', specificationId] })
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при удалении документа: ' + (err.message || 'неизвестная ошибка'))
    }
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadMutation.mutate(e.target.files)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (canEdit) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (canEdit && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadMutation.mutate(e.dataTransfer.files)
    }
  }

  const handleDelete = (docId: number, filename: string) => {
    if (confirm(`Вы уверены, что хотите удалить документ "${filename}"?`)) {
      deleteMutation.mutate(docId)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            📂 Документы устройства
            <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {documents.length}
            </span>
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Паспорта, инструкции, сертификаты и прочая документация для данного типа устройства
          </p>
        </div>
        {canEdit && (
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              multiple
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-150 disabled:opacity-50 flex items-center gap-1.5"
            >
              <span>➕ Загрузить документы</span>
            </button>
          </div>
        )}
      </div>

      {uploadProgress && (
        <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm flex items-center gap-2 font-medium animate-pulse">
          <span>🔄</span>
          <span>{uploadProgress}</span>
        </div>
      )}

      {/* Зона Drag and Drop */}
      {canEdit && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 mb-6 ${
            isDragOver
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-200 hover:border-gray-300 text-gray-500'
          }`}
        >
          <div className="flex flex-col items-center justify-center gap-2">
            <span className="text-3xl">📤</span>
            <p className="font-medium text-sm text-gray-700">
              Перетащите файлы сюда или{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-blue-600 hover:underline font-semibold"
              >
                выберите на диске
              </button>
            </p>
            <p className="text-xs text-gray-400">
              Поддерживается загрузка нескольких файлов любого формата
            </p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Загрузка документов...</div>
      ) : error ? (
        <div className="text-center py-8 text-red-600">Ошибка при получении документов</div>
      ) : documents.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {documents.map((doc) => {
            const icon = getFileIcon(doc.original_filename, doc.mime_type)
            const downloadUrl = `/api/specification-documents/${doc.id}?mode=download`
            const viewUrl = `/api/specification-documents/${doc.id}?mode=view`
            
            // Определяем, можно ли открыть файл в браузере (изображения, pdf)
            const ext = doc.original_filename.split('.').pop()?.toLowerCase() || ''
            const canViewInline = doc.mime_type?.startsWith('image/') || ext === 'pdf'

            return (
              <div
                key={doc.id}
                className="group relative flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all duration-200"
              >
                <span className="text-2xl mt-0.5">{icon}</span>
                <div className="flex-1 min-w-0 pr-6">
                  <p className="text-sm font-semibold text-gray-800 truncate mb-0.5" title={doc.original_filename}>
                    {doc.original_filename}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5">
                    <span>{getFileSizeString(doc.file_size)}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                    <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                  </p>
                  
                  {/* Ссылки/кнопки действий */}
                  <div className="flex gap-2.5 mt-2">
                    <a
                      href={downloadUrl}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                    >
                      ⬇️ Скачать
                    </a>
                    {canViewInline && (
                      <a
                        href={viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-semibold text-gray-600 hover:text-gray-800 flex items-center gap-0.5"
                      >
                        👁️ Просмотр
                      </a>
                    )}
                  </div>
                </div>

                {/* Кнопка удаления */}
                {canEdit && (
                  <button
                    onClick={() => handleDelete(doc.id, doc.original_filename)}
                    disabled={deleteMutation.isPending}
                    className="absolute top-3 right-3 text-gray-400 hover:text-red-600 rounded-lg p-1 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all duration-150"
                    title="Удалить документ"
                  >
                    🗑️
                  </button>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <p className="text-sm">Нет прикрепленных документов</p>
          {canEdit && <p className="text-xs mt-1">Загрузите инструкции или паспорта устройства выше</p>}
        </div>
      )}
    </div>
  )
}
